import type { OrderListFlag, OrderListKind } from '@fitting-room/contracts'
import { ORDER_LIST_FLAGS, ORDER_LIST_KINDS } from '@fitting-room/contracts/order-math'
import { BOUTIQUE_LOCALE } from './boutique'
import { uiStrings } from './strings'

const t = uiStrings(BOUTIQUE_LOCALE)

/**
 * The order list's filters beyond status: their words, and how they read
 * from and write to the URL. What each one means is `orderListFlags` in
 * `domain`, worked out by the API over every order before the list is paged.
 * `docs/22-back-office.md` § "Orders".
 */

export const ORDER_FLAG_LABEL: Record<OrderListFlag, string> = {
  pending: 'Pending',
  balance_due: 'Balance due',
  not_ordered: t.orderNotOrdered,
  not_arrived: 'Not arrived yet',
  ready_for_pickup: 'Ready for pickup',
  pickup_soon: 'Pickup within 14 days',
  wedding_soon: 'Wedding within 60 days',
}

/** What each chip means, for its tooltip: the definitions are not obvious from two words. */
export const ORDER_FLAG_HINT: Record<OrderListFlag, string> = {
  pending: 'Not canceled, and not yet both paid in full and picked up',
  balance_due: 'Not canceled, and still owes money',
  not_ordered: t.orderNotOrderedFilterHint,
  not_arrived: 'A special order or ordered-in piece that hasn’t arrived from the supplier',
  ready_for_pickup: 'Something arrived or on hold in the boutique, waiting for the customer',
  pickup_soon: 'Not canceled, not all picked up, and the expected pickup is today or in the next 14 days',
  wedding_soon: 'Not canceled, and the wedding is today or in the next 60 days',
}

/** The owner's word for `made_to_order` is "Special order". */
export const ORDER_KIND_LABEL: Record<OrderListKind, string> = {
  made_to_order: 'Special order',
  off_the_rack: 'Off the rack',
}

export const ORDER_FLAG_ORDER: readonly OrderListFlag[] = ORDER_LIST_FLAGS
export const ORDER_KIND_ORDER: readonly OrderListKind[] = ORDER_LIST_KINDS

/** The URL's comma list, keeping only the values this list knows — a stale link can't send the API junk. */
export function knownValues<T extends string>(raw: string | null, known: readonly T[]): T[] {
  return (raw ?? '')
    .split(',')
    .filter((value): value is T => (known as readonly string[]).includes(value))
}

/** `value` on or off in `list`, in the order the chips are drawn. */
export function toggleValue<T extends string>(list: readonly T[], value: T, order: readonly T[]): T[] {
  const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  return order.filter((v) => next.includes(v))
}
